import React from "react";
import { useTranslation } from "react-i18next";
import { ApiInternal } from "src/api/api_internal";
import { SimpleGroup } from "src/api/generated/frontend_internal_v1";
import { ConfirmDialog } from "src/components/base/confirm-dialog";
import { notify } from "src/components/base/toast";

/**
 * The properties for {@link DeleteGroupDialog}
 */
export type DeleteGroupDialogProps = {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback for closing the popup */
    onClose: () => void;
    /** Callback for successful deletion */
    onDeletion: () => void;
    /** The group */
    group: SimpleGroup | undefined;
};

/**
 * A dialog to delete a group.
 *
 * Renders any backend-provided errors (e.g. blocking access-rule references)
 * inline above the actions instead of throwing — the confirm button stays
 * available so the user can retry once they resolve the issue.
 */
export default function DeleteGroupDialog(props: DeleteGroupDialogProps) {
    const [t] = useTranslation("dialog-delete-group");
    const [errors, setErrors] = React.useState<Array<string>>([]);

    React.useEffect(() => {
        if (!props.isOpen) setErrors([]);
    }, [props.isOpen]);

    if (!props.group) return null;

    return (
        <ConfirmDialog
            open={props.isOpen}
            onClose={props.onClose}
            onConfirm={async () => {
                if (!props.group) return;
                const res = await ApiInternal.groups.local.delete(props.group.uuid);
                if (res.result === "Err") {
                    const next: Array<string> = [];
                    if (res.error.access_rules) {
                        next.push(t("error.access-rules-with-group", { count: res.error.access_rules }));
                    }
                    setErrors(next);
                    return;
                }
                notify.success(t("toast.group-deleted-successfully"));
                props.onDeletion();
            }}
            title={t("heading.delete-group", { group: props.group.name })}
            description={t("description.delete-group")}
            confirmLabel={t("button.delete-group")}
        >
            {errors.length > 0 && (
                <div className={"mt-3 flex flex-col gap-1 text-sm text-red-500 dark:text-red-400"}>
                    {errors.map((err, idx) => (
                        <span key={idx}>{err}</span>
                    ))}
                </div>
            )}
        </ConfirmDialog>
    );
}
